import { Either, Maybe } from 'monet';
import LeftResponse from '../LeftResponse';

/**
 * @description Запрос на редактирование настроек клиники.
 * @param {string}    branchName                  Название филиала.
 * @param {string}    clinicAddress               Адрес клиники.
 * @param {string}    clinicCity                  Город клиники.
 * @param {string}    clinicINN                   ИНН клиники.
 * @param {string}    clinicBIN                   БИН клиники.
 * @param {string}    clinicKPP                   КПП клиники.
 * @param {string}    clinicName                  Название клиники.
 * @param {string}    directorName                Имя руководителя.
 * @param {string}    directorPatronymic          Отчество руководителя.
 * @param {string}    directorSurname             Фамилия руководителя.
 * @param {string}    egiszConfidentialityId      Идентификатор конфиденциальности.
 * @param {boolean}   isTradeAcquiringEnabled     Торговый эквайринг подключен.
 * @param {string}    legalName                   Юр. лицо.
 * @param {string}    licenseIssuedDate           Дата выдачи лицензии.
 * @param {string}    licenseIssuer               Кем выдана лицензия.
 * @param {string}    licenseSerialAndNumber      Серия и номер лицензии.
 * @param {string}    phoneNumber                 Номер телефона.
 * @param {File|null} logo                        Логотип.
 * @param {string}    defaultVat                  НДС по умолчанию.
 * @param {string}    sno                         Система налогообложения.
 * @param {string}    taxAuthorityCode            Код налогового органа.
 * @param {string}    clinicOGRN                  ОГРН/ОГРНИП
 * @param {string}    clinicRightFormRegistration ОГРН/ОГРНИП
 * @param {string}    needLinkPatientsMainBranch  Необходимо перенести пациентов основного филиала.
 * @constructor
 */
export function EditClinicSettingsRequest({
  branchName,
  clinicAddress,
  clinicCity,
  clinicINN,
  clinicBIN,
  clinicKPP,
  clinicName,
  directorName,
  directorPatronymic,
  directorSurname,
  egiszConfidentialityId,
  isTradeAcquiringEnabled,
  legalName,
  licenseIssuedDate,
  licenseIssuer,
  licenseSerialAndNumber,
  phoneNumber,
  logo,
  defaultVat,
  sno,
  taxAuthorityCode,
  clinicOGRN,
  clinicRightFormRegistration,
  needLinkPatientsMainBranch,
}) {
  this.toQuery = (method) => {
    const form = new FormData();
    form.append('branch_name', branchName);
    form.append('clinic_address', clinicAddress);
    form.append('clinic_city', clinicCity || '');
    form.append('clinic_inn', clinicINN);
    form.append('clinic_bin', clinicBIN);
    form.append('clinic_kpp', clinicKPP);
    form.append('clinic_name', clinicName);
    form.append('director_name', directorName);
    form.append('director_patronymic', directorPatronymic);
    form.append('director_surname', directorSurname);
    form.append('egisz_confidentiality_id', egiszConfidentialityId);
    form.append('legal_name', legalName);
    form.append('license_issued_date', licenseIssuedDate);
    form.append('license_issuer', licenseIssuer);
    form.append('license_serial_and_number', licenseSerialAndNumber);
    form.append('phone_number', phoneNumber);
    if (logo) {
      form.append('logo', logo);
    }
    form.append('default_vat', defaultVat);
    form.append('sno', sno);
    form.append('is_trade_acquiring_enabled', isTradeAcquiringEnabled ? 1 : 0);
    form.append('tax_authority_code', taxAuthorityCode);
    form.append('_method', method);
    form.append('clinic_ogrn', clinicOGRN);
    form.append('clinic_right_form_registration', clinicRightFormRegistration);
    form.append('need_link_patients_main_branch', needLinkPatientsMainBranch ? 1 : 0);
    return form;
  };
}

/**
 * @description Шлюз основных настроек.
 * @param {Axios}                 axios
 * @param {StorageChangeNotifier} changeNotifier
 * @constructor
 */
export default function SettingGateway(axios, changeNotifier) {
  /**
   * @description Добавить филиал.
   * @param {EditClinicSettingsRequest} request Данные филиала.
   * @returns {Promise<Either>}
   */
  this.addBranch = (request) => axios
    .post('/api/branches', request.toQuery('post'))
    .then(
      (response) => {
        changeNotifier.push(response.data.changed_for || []);
        return Either.Right(response.data);
      },
      (error) => {
        if (error.isAxiosError && error.response.status === 400) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Проверить ключ приглашения в сеть филиала.
   * @param {string} key Значение ключа.
   * @returns {Promise<Either>}
   */
  this.checkInvitationKeyToBranch = (key) => axios
    .get(`/api/branch-invitations/${key}`)
    .then(
      (response) => {
        changeNotifier.push(response.data.changed_for || []);
        const { next_check_ms: nextCheck = null, title } = response.data;
        return Either.Right({
          nextCheck,
          status: title.includes('присоединен к сети') ? 'completed' : 'any',
          title,
        });
      },
      (error) => {
        if (error.isAxiosError && error.response.status === 400) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Создать ключ для приглашения в сеть.
   * @returns {Promise<Either>}
   */
  this.createInvitationKeyToBranch = () => axios
    .post('/api/branch-invitations')
    .then(
      (response) => Either.Right(response.data),
      (error) => {
        if (error.isAxiosError && error.response.status === 400) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Деактивировать ключ приглашения к сети филиалов.
   * @param {string} requestedKey Ключ приглашения.
   * @returns {Promise<Either>}
   */
  this.deactivateInvitationKeyToBranch = (requestedKey) => axios
    .put(`/api/branch-invitations/${requestedKey}/deactivation`)
    .then((response) => Either.Right(response.data));

  /**
   * @description Отредактировать настройки дополнительного филиала.
   * @param {string}                    id      Идентификатор филиала.
   * @param {EditClinicSettingsRequest} request Запрос изменений.
   * @returns {Promise<Either>}
   */
  this.editAdditionalBranch = (id, request) => axios
    .post(`/api/branches/${id}/settings`, request.toQuery('put'))
    .then(
      (response) => {
        changeNotifier.push(response.data.changed_for || []);
        return Either.Right(response.data);
      },
      (error) => {
        if (
          error.isAxiosError && (
            error.response.status === 400
            || error.response.status === 403
            || error.response.status === 404
          )
        ) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Получить наличие ошибок настроек клиники.
   * @returns {Promise<Maybe>}
   */
  this.getHasClinicSettingsErrors = () => axios
    .get('/api/settings-existence-errors')
    .then(async (response) => Maybe.fromEmpty({
      hasSettingsErrors: response.data.has_clinic_errors,
    }));

  /**
   * @description Присоединиться к сети филиалов.
   * @param {string} requestedKey Ключ приглашения.
   * @returns {Promise<Either>}
   */
  this.joinToBranch = (requestedKey) => axios
    .post(`/api/branch-invitations/${requestedKey}/branches`)
    .then(
      (response) => {
        changeNotifier.push(response.data.changed_for || []);
        return Either.Right(response.data);
      },
      (error) => {
        if (error.isAxiosError && error.response.status === 400) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Получить текущие настройки клиники.
   * @param {function} branchMapper  Отображатель данных филиала.
   * @param {function} generalMapper Отображатель общих данных.
   * @returns {Promise<Either>}
   */
  this.viewClinicSettings = (branchMapper, generalMapper) => axios
    .get('/api/settings')
    .then(
      async (response) => {
        const formatIssuedDate = (date) => {
          const [y, m, d] = date.split('-');
          return [d, m, y].join('/');
        };
        const collectBranchFields = (branch) => [
          branch.address,
          branch.allowed_actions,
          branch.city,
          branch.inn,
          branch.bin,
          branch.kpp,
          branch.manager_name,
          branch.manager_patronymic,
          branch.manager_surname,
          branch.egisz_confidentiality_id,
          branch.id,
          branch.is_trade_acquiring,
          branch.legal_name,
          branch.license_issued_date ? formatIssuedDate(branch.license_issued_date) : '',
          branch.license_issuer,
          branch.license_serial_and_number,
          branch.phone_number,
          branch.vat_title,
          branch.vat_value,
          branch.sno_title,
          branch.sno_value,
          branch.tax_authority_code,
          branch.ogrn,
          branch.right_form_registration,
          branch.tags,
          branch.title,
        ];
        const {
          actions,
          main_branch: mainBranch,
          errors,
          inactive_actions: inactiveActions,
        } = response.data;
        return Either.right({
          actions,
          additionalBranches: (response.data.additional_branches || [])
            .map((branch) => branchMapper(...collectBranchFields(branch))),
          errors,
          general: generalMapper(
            response.data.clinic_name,
            response.data.logo_url,
          ),
          inactiveActions,
          mainBranch: branchMapper(...collectBranchFields(mainBranch)),
        });
      },
      async (error) => {
        if (error.isAxiosError && error.response.status === 403) {
          const causes = error.response.data.causes || [];
          const { title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Отправить запрос на изменение основной информации о клинике.
   * @param {EditClinicSettingsRequest} request
   * @returns {Promise<Either>}
   */
  this.sendUpdateClinicInfo = (request) => axios
    .post('/api/settings', request.toQuery('put'))
    .then(
      async (response) => {
        changeNotifier.push(response.data.changed_for || []);
        return Either.Right(response.data);
      },
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Получить шаги инструкции.
   * @returns {Promise<Maybe>}
   */
  this.viewInstructionSteps = () => axios
    .get('/api/instruction-steps')
    .then(
      async (response) => Maybe.some(response.data.steps),
      async () => Maybe.none(),
    );

  /**
   * @description Получить шаги инструкции.
   * @returns {Promise<Maybe>}
   */
  this.downloadLogo = () => axios
    .get('/api/download-logo', { responseType: 'blob' })
    .then(
      async (response) => Maybe.some(response.data),
      async () => Maybe.none(),
    );
}
